// Minimal markdown -> HTML for the AI agent panel.
//
// Handles: headings, paragraphs, bold/italic, inline code, code blocks,
// links, ordered/unordered lists, and pipe-style tables.
//
// The output is rendered with v-html, and the input is untrusted LLM
// output (tool results can echo back account names, notes, etc). So we
// escape all raw HTML and only ever emit a fixed set of tags. Link hrefs
// are passed through safeHref(), which drops anything that is not http,
// https, mailto, or a same-origin path. That blocks javascript: URLs.

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Allow only safe URL schemes. Anything else (javascript:, data:, vbscript:,
// protocol-relative //host, or a mangled value) collapses to '#' so a click
// does nothing. Quotes are percent-encoded so the value can never break out
// of an HTML attribute.
export function safeHref(raw) {
  const href = String(raw).trim()
  const isInternalPath = href.startsWith('/') && !href.startsWith('//')
  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href) || isInternalPath) {
    return href.replace(/"/g, '%22').replace(/'/g, '%27')
  }

  return '#'
}

function inline(text) {
  let out = escapeHtml(text)
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>')
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, href) => {
    return `<a href="${safeHref(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`
  })
  return out
}

function renderTable(rows) {
  const header = rows[0].slice(1, -1).split('|').map(s => s.trim())
  const align = rows[1].slice(1, -1).split('|').map(s => {
    const t = s.trim()
    if (t.startsWith(':') && t.endsWith(':')) {
      return 'center'
    }

    if (t.endsWith(':')) {
      return 'right'
    }

    return 'left'
  })
  const body = rows.slice(2).map(r => r.slice(1, -1).split('|').map(s => s.trim()))

  const thead = '<thead><tr>' + header.map((h, i) => `<th style="text-align:${align[i] || 'left'}">${inline(h)}</th>`).join('') + '</tr></thead>'
  const tbody = '<tbody>' + body.map(row =>
    '<tr>' + row.map((c, i) => `<td style="text-align:${align[i] || 'left'}">${inline(c)}</td>`).join('') + '</tr>'
  ).join('') + '</tbody>'

  return `<table class="ai-md-table">${thead}${tbody}</table>`
}

export function renderMarkdown(input) {
  if (!input) {
    return ''
  }

  const lines = input.split('\n')
  const out = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim()
      const codeLines = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }

      i++
      out.push(`<pre class="ai-md-code"><code data-lang="${escapeHtml(lang)}">${escapeHtml(codeLines.join('\n'))}</code></pre>`)
      continue
    }

    if (/^#{1,3}\s/.test(line)) {
      const level = line.match(/^#+/)[0].length
      out.push(`<h${level + 2}>${inline(line.replace(/^#+\s+/, ''))}</h${level + 2}>`)
      i++
      continue
    }

    if (line.startsWith('|') && lines[i + 1] && lines[i + 1].startsWith('|') && /^[|\s:-]+$/.test(lines[i + 1])) {
      const tableRows = []
      while (i < lines.length && lines[i].startsWith('|')) {
        tableRows.push(lines[i])
        i++
      }

      out.push(renderTable(tableRows))
      continue
    }

    if (/^(\s*[-*]\s)/.test(line)) {
      const items = []
      while (i < lines.length && /^(\s*[-*]\s)/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`)
        i++
      }

      out.push(`<ul class="ai-md-list">${items.join('')}</ul>`)
      continue
    }

    if (/^(\s*\d+\.\s)/.test(line)) {
      const items = []
      while (i < lines.length && /^(\s*\d+\.\s)/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`)
        i++
      }

      out.push(`<ol class="ai-md-list">${items.join('')}</ol>`)
      continue
    }

    if (line.trim() === '') {
      i++
      continue
    }

    const paraLines = [lines[i]]
    i++
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('```') && !lines[i].startsWith('|') && !/^(\s*[-*]\s|\s*\d+\.\s|#{1,3}\s)/.test(lines[i])) {
      paraLines.push(lines[i])
      i++
    }

    out.push(`<p>${inline(paraLines.join(' '))}</p>`)
  }

  return out.join('\n')
}
